import React from "react";
import { useDropzone } from "react-dropzone";
import { FiUploadCloud, FiFile, FiX } from "react-icons/fi";
import useImageUpload from "../utils/UploadImage";

function Upload({ userName, projectInput }) {
  const [files, setFiles] = React.useState([]);

  const { handleUpload, uploadProgress, retryFailedBatches, isLoading } = useImageUpload({
    projectInput,
    userName,
    setProjectName: () => { },
    setNameConfirmed: () => { },
  });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "application/zip": [".zip"],
      "image/*": [".jpg", ".jpeg", ".png", ".gif", ".bmp"],
    },
    multiple: true,
    noClick: false,
    noKeyboard: false,
    onDrop: async (acceptedFiles) => {
      setFiles(acceptedFiles);

      // Check if any file is a directory (folder upload)
      const isFolderUpload = acceptedFiles.some(
        (file) =>
          file.webkitRelativePath && file.webkitRelativePath.includes("/")
      );

      if (
        acceptedFiles.length === 1 &&
        acceptedFiles[0].name.endsWith(".zip")
      ) {
        // ZIP file handling
        handleUpload(acceptedFiles[0]);
      } else {
        // Images or folder of images
        handleUpload(acceptedFiles);
      }
    },
    useFsAccessApi: true,
  });

  const removeFile = () => {
    setFiles([]);
  };

  return (
    <div className="max-w-md mx-auto p-6">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${isDragActive
            ? "border-blue-500 bg-blue-50"
            : "border-gray-300 hover:border-blue-400"
          }`}
      >
        <input
          {...getInputProps({
            webkitdirectory: "true",
            directory: "true",
          })}
        />

        <div className="flex flex-col items-center justify-center space-y-4">
          <FiUploadCloud className="w-12 h-12 text-gray-400" />
          {isDragActive ? (
            <p className="text-blue-500 font-medium">
              Drop files or folder here
            </p>
          ) : (
            <>
              <p className="font-medium text-gray-700">
                Drag & drop a ZIP, folder, or image(s) here
              </p>
              <p className="text-sm text-gray-500">
                Accepts .zip or image files (JPG, PNG, etc)
              </p>
            </>
          )}
        </div>
      </div>

      {/* Progress Section */}
      {uploadProgress.dummyProgress > 0 && (
        <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="space-y-3">
            <div className="relative pt-1">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold text-gray-700">Upload Progress</div>
                <div className="text-sm font-medium text-blue-600">{uploadProgress.dummyProgress.toFixed(1)}%</div>
              </div>
              <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-blue-100">
                <div
                  style={{ width: `${uploadProgress.dummyProgress}%` }}
                  className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500 transition-all duration-300"
                ></div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-sm text-gray-600">
                <span className="font-medium">Speed:</span> {uploadProgress.speed} MB/s
              </div>
              <div className="text-sm text-gray-600">
                <span className="font-medium">ETA:</span> {uploadProgress.eta}
              </div>
            </div>
            {uploadProgress.failedBatches.length > 0 && (
              <button
                onClick={() => retryFailedBatches(uploadProgress.failedBatches)}
                className="w-full mt-2 px-4 py-2 bg-yellow-100 text-yellow-800 rounded-md hover:bg-yellow-200 transition-colors duration-200 font-medium text-sm"
              >
                Retry Failed Batches
              </button>
            )}
          </div>
        </div>
      )}

      {files.length > 0 && (
        <div className="mt-4 p-3 bg-gray-50 rounded-md">
          {files.map((file, idx) => (
            <div key={idx} className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <FiFile className="text-gray-500" />
                <span className="text-sm font-medium text-gray-700 truncate max-w-xs">
                  {file.name}
                </span>
              </div>
            </div>
          ))}
          <button
            onClick={removeFile}
            className="text-gray-500 hover:text-gray-700 mt-2 flex items-center space-x-1"
            aria-label="Remove all"
          >
            <FiX />
            <span>Remove All</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default Upload;